import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Toolbar,
  Typography
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  Mail as MailIcon,
  Phone as PhoneIcon
} from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { sentenceCase } from "change-case";
import chroma from "chroma-js";
import { groupBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { BaseLayout, Flaticon, Header, Loader, NoData } from "../components";
import CardItem from "../components/CardItem";
import { types } from "../ducks";
import { getContactFaqs, getFaqs, getVideoFaqs } from "../ducks/actions";
import { VideoFaqDialog } from "../features/help-desk";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  contactBox: {
    height: "100%",
    borderRadius: theme.spacing(1),
    border: "solid 1px",
    borderColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  contactBoxText: {
    flexGrow: 1
  },
  accordionRoot: {
    backgroundColor: "transparent",
    "&:before": {
      height: 0
    }
  },
  menuItem: {
    borderRadius: theme.spacing(3),
    "&:hover": {
      backgroundColor: chroma(theme.palette.primary.main).alpha(0.05).rgba()
    }
  },
  menuItemSelected: {
    backgroundColor: `rgba(${chroma(theme.palette.primary.main).alpha(0.1).rgba()}) !important`,
    color: theme.palette.primary.main
  }
}));

const HelpDeskPage = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedVideoFaq, setSelectedVideoFaq] = useState(null);

  useEffect(() => {
    dispatch(getContactFaqs());
    dispatch(getFaqs());
    dispatch(getVideoFaqs());
  }, [dispatch]);

  const {
    data: { data: contactFaqs },
    loading: contactFaqsLoading
  } = useQuery({ type: types.GET_CONTACT_FAQS, defaultData: { data: [] } });

  const {
    data: { data: faqs },
    loading: faqsLoading
  } = useQuery({ type: types.GET_FAQS, defaultData: { data: [] } });

  const {
    data: { data: videoFaqs },
    loading: videoFaqsLoading
  } = useQuery({ type: types.GET_VIDEO_FAQS, defaultData: { data: [] } });

  useEffect(() => {
    if (faqs?.length) {
      setSelectedTopic(faqs[0].topic);
    }
  }, [dispatch, faqs]);

  const faqsByTopic = groupBy(faqs, ({ topic }) => topic);

  return (
    <BaseLayout className={classes.root}>
      <Header
        icon="lifesaver"
        title={intl.formatMessage({
          id: "Pages.HelpDeskPage.HelpDesk",
          defaultMessage: "Help Desk"
        })}
      />
      <Loader loading={contactFaqsLoading}>
        <NoData data={contactFaqs}>
          <Grid container spacing={3}>
            {contactFaqs.map(({ id, title, description, email, phone }, i) => (
              <Grid key={id} item xs={12} lg={6}>
                <div className={classes.contactBox}>
                  <Flaticon
                    icon={i % 2 ? "support-1" : "support"}
                    margin={1}
                    fontSize="3rem"
                    color="primary"
                  />
                  <Typography variant="h5" gutterBottom>
                    <strong>{title}</strong>
                  </Typography>
                  <Typography variant="body1" className={classes.contactBoxText}>
                    {description}
                  </Typography>
                  <Toolbar>
                    {email && (
                      <Link href={`mailto:${email}`} component={IconButton}>
                        <MailIcon color="primary" />
                      </Link>
                    )}
                    {phone && (
                      <Link href={`tel:${phone}`} component={IconButton}>
                        <PhoneIcon color="primary" />
                      </Link>
                    )}
                  </Toolbar>
                </div>
              </Grid>
            ))}
          </Grid>
        </NoData>
      </Loader>
      <Loader loading={faqsLoading}>
        <NoData data={faqsByTopic}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <List>
                {Object.keys(faqsByTopic).map(topic => (
                  <ListItem
                    key={topic}
                    button
                    selected={selectedTopic === topic}
                    classes={{ root: classes.menuItem, selected: classes.menuItemSelected }}
                    onClick={() => {
                      setSelectedTopic(topic);
                    }}
                  >
                    <ListItemText
                      primary={
                        selectedTopic === topic ? (
                          <strong>{sentenceCase(topic)}</strong>
                        ) : (
                          sentenceCase(topic)
                        )
                      }
                      primaryTypographyProps={{ color: "primary" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} sm={9}>
              {(selectedTopic ? faqsByTopic[selectedTopic] : []).map(({ id, question, answer }) => (
                <Accordion key={id} className={classes.accordionRoot} elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                    <Typography variant="body1">
                      <strong>{question}</strong>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1">{answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </NoData>
      </Loader>
      <Typography variant="h5" gutterBottom>
        {intl.formatMessage({
          id: "Pages.HelpDeskPage.VideoTutorial",
          defaultMessage: "Video Tutorial"
        })}
      </Typography>
      <Loader loading={videoFaqsLoading}>
        <NoData data={videoFaqs}>
          <Grid container spacing={3}>
            {videoFaqs.map(({ id, topic, videoUrl }) => (
              <Grid key={id} item xs={12} sm={3}>
                <CardItem
                  title={topic}
                  videoUrl={videoUrl}
                  onClick={() => {
                    setSelectedVideoFaq({ id, topic, videoUrl });
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </NoData>
      </Loader>
      {selectedVideoFaq && (
        <VideoFaqDialog
          videoFaq={selectedVideoFaq}
          onClose={() => {
            setSelectedVideoFaq(null);
          }}
        />
      )}
    </BaseLayout>
  );
};

export default HelpDeskPage;
